import { useEffect, useState } from "react";
import { Moment } from "moment";
import { MiButton, MiIcon } from "@miplatform/components";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { useAnchor } from "@miplatform/hooks";
import { ButtonSizes, ButtonVariants } from "@miplatform/types";
import { formatDate } from "@miplatform/utils";
import {
  mdiCalendarBlankOutline,
  mdiChevronDown,
  mdiClockOutline,
} from "@mdi/js";
import {
  Box,
  Divider,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import { calculateAssignmentsAutoPublishRemainingTime } from "./AssignmentsCalendar.util";

interface PublishActionRowProps {
  scheduledForTime: Moment;
  isAutoPublishOn?: boolean;
  onPublishNow?: () => void;
  onPauseAutoPublish?: () => void;
  onResumeAutoPublish?: () => void;
}

export const AssignmentsPublishActions = ({
  scheduledForTime,
  isAutoPublishOn = true,
  onPublishNow,
  onPauseAutoPublish,
  onResumeAutoPublish,
}: PublishActionRowProps) => {
  const theme = useTheme();
  const [anchorEl, handleMenuOpen, handleMenuClose] = useAnchor();
  const isMenuOpen = Boolean(anchorEl);

  const handlePause = () => {
    handleMenuClose();
    onPauseAutoPublish?.();
  };

  const handleResume = () => {
    handleMenuClose();
    onResumeAutoPublish?.();
  };

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <PublishCountDownTimer
        targetTime={scheduledForTime}
        isAutoPublishOn={isAutoPublishOn}
      />
      <Divider
        orientation="vertical"
        flexItem
        sx={{ height: 28, alignSelf: "center", mx: 2 }}
      />
      <Box
        display="flex"
        alignItems="flex-start"
        flexDirection="column"
        gap={0.75}
      >
        <Box display="flex" alignItems="center" gap={1} mt={0.25}>
          <MiIcon
            path={mdiCalendarBlankOutline}
            size={0.5}
            color={theme.palette.grey[600]}
          />
          <Typography fontSize={13} color="text.secondary" lineHeight={1.2}>
            Scheduled for
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column">
          <Typography
            fontSize={14}
            fontWeight={700}
            color={theme.palette.primary.main}
            lineHeight={1.3}
          >
            {isAutoPublishOn
              ? formatDate(scheduledForTime, DATE_TIME_FORMATS.FORMAT_12_HOUR)
              : "N/A"}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" ml={2}>
        <MiButton
          title={isAutoPublishOn ? "Auto Publish On" : "Auto Publish Off"}
          size={ButtonSizes.Small}
          variant={ButtonVariants.Contained}
          onClick={handleMenuOpen}
          sx={{ padding: theme.spacing(0, 2), paddingLeft: theme.spacing(4) }}
          endIcon={{
            path: mdiChevronDown,
            iconColor: "#fff",
            iconSize: 0.8,
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: 30,
              width: "1px",
              backgroundColor: theme.palette.grey.accent,
              marginLeft: theme.spacing(1.5),
              marginRight: theme.spacing(0.5),
            }}
          />
        </MiButton>
        <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={handleMenuClose}>
          <MenuItem
            onClick={handlePause}
            sx={{
              color: theme.palette.error.main,
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Pause Auto Publish
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleResume}
            sx={{
              fontSize: 14,
            }}
          >
            Resume Auto Publish
          </MenuItem>
        </Menu>
      </Box>
      <MiButton
        size={ButtonSizes.Small}
        variant={ButtonVariants.Outlined}
        onClick={onPublishNow}
      >
        Publish Now
      </MiButton>
    </Box>
  );
};

interface PublishCountdownTimerProps {
  targetTime: Moment;
  isAutoPublishOn?: boolean;
}

const PublishCountDownTimer = ({
  targetTime,
  isAutoPublishOn = true,
}: PublishCountdownTimerProps) => {
  const theme = useTheme();
  const [timeLeft, setTimeLeft] = useState<string>(() =>
    calculateAssignmentsAutoPublishRemainingTime(targetTime)
  );

  useEffect(() => {
    if (!isAutoPublishOn) return () => null;

    setTimeLeft(calculateAssignmentsAutoPublishRemainingTime(targetTime));

    const timer = setInterval(() => {
      setTimeLeft(calculateAssignmentsAutoPublishRemainingTime(targetTime));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetTime, isAutoPublishOn]);

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      flexDirection="column"
      gap={0.75}
    >
      <Box display="flex" alignItems="center" gap={1} mt={0.25}>
        <MiIcon
          path={mdiClockOutline}
          size={0.5}
          color={theme.palette.grey[600]}
        />
        <Typography fontSize={13} color="text.secondary" lineHeight={1.2}>
          Publishes in
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column">
        <Typography
          fontSize={14}
          fontWeight={700}
          color={theme.palette.primary.main}
          lineHeight={1.3}
        >
          {isAutoPublishOn ? timeLeft : "N/A"}
        </Typography>
      </Box>
    </Box>
  );
};
